import {
  type AdmitOnboardingCaseRequest,
  type CancelOnboardingCaseRequest,
  type CancelOnboardingPhaseTasksRequest,
  type CompleteOnboardingCaseRequest,
  type CreateOnboardingCaseRequest,
  type OnboardingCaseDetail,
  type OnboardingCaseListQuery,
  type PatchOnboardingCaseDatesRequest,
  type ReopenOnboardingCaseRequest,
  type StartOnboardingCaseRequest,
  onboardingCaseDetailSchema,
  onboardingCaseListResponseSchema,
} from '@gb-ticket/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api';
import { toSearchParams } from '@/lib/format';

export const onboardingCasesKey = (query: OnboardingCaseListQuery) =>
  ['onboarding', 'cases', query] as const;

export const onboardingCaseKey = (id: string) => ['onboarding', 'case', id] as const;

export function useOnboardingCases(query: OnboardingCaseListQuery) {
  return useQuery({
    queryKey: onboardingCasesKey(query),
    queryFn: () =>
      apiFetch(
        `/onboarding/cases${toSearchParams({
          page: query.page,
          pageSize: query.pageSize,
          sortBy: query.sortBy,
          sortOrder: query.sortOrder,
          kind: query.kind,
          status: query.status,
          subjectUserId: query.subjectUserId,
          departmentId: query.departmentId,
          siteId: query.siteId,
          q: query.q,
        })}`,
        onboardingCaseListResponseSchema,
      ),
  });
}

export function useOnboardingCase(id: string) {
  return useQuery({
    queryKey: onboardingCaseKey(id),
    queryFn: () => apiFetch(`/onboarding/cases/${id}`, onboardingCaseDetailSchema),
    enabled: Boolean(id),
  });
}

export function useCreateOnboardingCase() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateOnboardingCaseRequest) =>
      apiFetch('/onboarding/cases', onboardingCaseDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (data) => {
      await rememberCase(client, data);
    },
  });
}

export function useStartOnboardingCase() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: StartOnboardingCaseRequest }) =>
      apiFetch(`/onboarding/cases/${id}/start`, onboardingCaseDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (data) => {
      await rememberCase(client, data);
    },
  });
}

export function useAdmitOnboardingCase() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: AdmitOnboardingCaseRequest }) =>
      apiFetch(`/onboarding/cases/${id}/admit`, onboardingCaseDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (data) => {
      await rememberCase(client, data);
    },
  });
}

export function useCompleteOnboardingCase() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CompleteOnboardingCaseRequest }) =>
      apiFetch(`/onboarding/cases/${id}/complete`, onboardingCaseDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (data) => {
      await rememberCase(client, data);
    },
  });
}

export function useCancelOnboardingCase() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CancelOnboardingCaseRequest }) =>
      apiFetch(`/onboarding/cases/${id}/cancel`, onboardingCaseDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (data) => {
      await rememberCase(client, data);
    },
  });
}

export function useCancelOnboardingPhaseTasks() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      phaseId,
      body,
    }: {
      id: string;
      phaseId: string;
      body: CancelOnboardingPhaseTasksRequest;
    }) =>
      apiFetch(
        `/onboarding/cases/${id}/phases/${encodeURIComponent(phaseId)}/cancel-open`,
        onboardingCaseDetailSchema,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: async (data) => {
      await rememberCase(client, data);
    },
  });
}

export function useReopenOnboardingCase() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ReopenOnboardingCaseRequest }) =>
      apiFetch(`/onboarding/cases/${id}/reopen`, onboardingCaseDetailSchema, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: async (data) => {
      await rememberCase(client, data);
    },
  });
}

export function usePatchOnboardingCaseDates() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchOnboardingCaseDatesRequest }) =>
      apiFetch(`/onboarding/cases/${id}/dates`, onboardingCaseDetailSchema, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: async (data) => {
      await rememberCase(client, data);
    },
  });
}

async function rememberCase(client: ReturnType<typeof useQueryClient>, data: OnboardingCaseDetail) {
  await Promise.all([
    client.invalidateQueries({ queryKey: ['onboarding', 'cases'] }),
    client.invalidateQueries({ queryKey: ['onboarding', 'case'] }),
    client.invalidateQueries({ queryKey: ['onboarding', 'tasks'] }),
  ]);
  client.setQueryData(onboardingCaseKey(data.id), data);
}
